import React, {memo, ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    CheckboxInput,
    DelayedSpinner,
    Fieldset,
    IconAndText,
    IconButton,
    Modal,
    NumberInput,
    Text,
    TextButton
} from "atmosfair-ui"
import './styles.css'
import '../../styles.css'
import cloneDeep from 'lodash/cloneDeep';
import {
    AircraftTypeResponse,
    AirlineResponse,
    AirportResponse,
    FlightCalculationRequestLeg,
    FlightsResponse
} from "../../../../api/model/activity-flight";
import {ActivityFlightState, Leg} from "../../../../types-enums/activity-flight";
import FlightSelector from "../flight-selector/FlightSelector";
import {apiSlice, useCalculateEmissionsMutation} from "../../../../redux/reducer/api";
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL, CALCULATION_TYPE} from "../../../../api/model/common";
import {GET_AIRCRAFTS, GET_FLIGHTS_BY_DATE_AND_AIRLINE} from "../../../../api/endpoints";
import {toast, TypeOptions} from "react-toastify";
import ToastWithTextOnly from "../../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {findIndex, isEqual} from "lodash"
import {theme} from '../../../../styles/theme';
import Cookies from "js-cookie";
import {isAirportResponse, isGeneralCalculatedFlightEmissionsResponse} from "../../../../utils/check/type-guards";
import {v4 as uuidv4} from "uuid";
import {getFirstUiLeg, getUiStopover} from "../../../../utils/getter/flight";
import {DateTime} from "luxon";
import {CalculatedEmissionsBackendResponse, FlightCalculatedEmissionsResponse} from "../../../../api/model/emissions";
import {KG_CO2} from "../../../../utils/string/units";
import usePrevious from "../../../../hooks/use-previous";
import AmbiguousFlightList from "../ambiguous-flight-list/AmbiguousFlightList";
import {TRAVEL_CLASSES} from "../../../../screens/activity/activity-form/flight";
import {ChooseActivityCommonProps, ChooseActivityWithDateCommonProps, createErrorCodeString} from "../../utils";
import AutocompleteQuery, {
    AUTOCOMPLETE_ACTIVITY_TYPE,
    AUTOCOMPLETE_QUERY_TYPE
} from '../../../../molecules/autocomplete-query/AutocompleteQuery';
import {COOKIE_NAME, FLIGHT_PROPERTIES} from "../../../../constants/default-values";
import {convertDateTimeToFlightDate} from "../../../../utils/converter/dates";
import {constructGetFlightByFlightNumberQuery} from "../../../../utils/string/api";

import {setCommonFlightActivityProps} from "../../../../utils/setter/activities";
import {convertUiAircraftTypeToApiAircraftType} from "../../../../utils/converter/activities";
import {
    formatDateString,
    generateTotalCo2EAndCo2EPerPersonString,
    NO_DATA_STRING,
    ROUTE_SEPARATOR
} from "../../../../utils/string/common";
import {createFlightTripPassengersCountString} from "../../../../utils/string/flight";
import {ActivityCommonProperties} from "../../../../types-enums/activity-common-properties";
import {PATH_SCREEN_STRING} from "../../../../routes/route-props";
import useFirstRender from "../../../../hooks/use-first-render";
import {activityHasReturnOption} from "../../../../utils/check/activities";
import clsx from "clsx";

export interface ChooseFlightProps extends ChooseActivityCommonProps, ChooseActivityWithDateCommonProps {
    flightToEdit: ActivityFlightState | null
    getCalculatedActivity: (activity: [ActivityFlightState, ActivityFlightState] | ActivityFlightState | null) => void
    disableDate: boolean,
    fromWhichScreen: PATH_SCREEN_STRING
}

export interface LegForUi extends Leg {
    errors: {
        airport: boolean
        flightNumber: boolean,
    },
}

interface FormErrorProps {
    numberPassengersError: boolean,
}

interface FormProps {
    legs: LegForUi[],
    returnFlight: boolean,
    passengerCount: number,
}

const setEmptyLeg = (id: string): LegForUi => ({
    airport: null,
    travelClass: null,
    aircraftType: null,
    airline: null,
    flightNumber: null,
    date: null,
    ownAirportTransfer: false,
    informationFromLoadedBundle: null,
    groupId: null,
    backendId: null,
    id,
    errors: {
        airport: false,
        flightNumber: false,
    }
})

const MAX_STOPOVERS = 10

const ChooseFlight = memo(function ({
                                        day,
                                        flightToEdit,
                                        getCalculatedActivity,
                                        resetForm,
                                        isEdit,
                                        openToDate,
                                        minDate,
                                        maxDate,
                                        disableDate,
                                        fromWhichScreen,
                                        maxPersons,
                                    }: ChooseFlightProps) {
    const [formData, setFormData] = useState<FormProps>({
        legs: [setEmptyLeg(uuidv4()), setEmptyLeg(uuidv4())],
        returnFlight: false,
        passengerCount: 1
    })
    const [aircrafts, setAircrafts] = useState<AircraftTypeResponse[]>([])
    const [aircraftsNetworkError, setAircraftsNetworkError] = useState(false)
    const [hideCalculationResults, setHideCalculationResults] = useState(false)
    const [formErrorData, setFormErrorData] = useState<FormErrorProps>({
        numberPassengersError: false,
    })
    const [onlyUsedAirportsForEmissionCalculation, setOnlyUsedAirportsForEmissionCalculation] = useState(false)
    const [disableCalculateButton, setDisableCalculateButton] = useState(false)
    const [modalVisible, setModalVisible] = useState(false)
    const [modalContent, setModalContent] = useState<FlightsResponse[]>([])
    const gettingFlightsByFlightNumberAndDate = useRef(false)
    const selectedFlightsViaFlightNumberAndDateFromModal = useRef<FlightsResponse[]>([])
    const allFlightsFoundViaFlightNumberAndDate = useRef<FlightsResponse[][]>([])
    const legIndexFromWhichModalWasOpened = useRef(-1)
    const legIndexFromWhichFlightWithFlightNumberAndDateWasRequested = useRef(-1)
    const flightsForRequestFormat = useRef<FlightCalculationRequestLeg[]>([])
    const airlineIataFromFlightNumber = useRef<string | null>(null)
    const {t, i18n} = useTranslation()
    const isFirstRender = useFirstRender()
    const {
        legs,
        returnFlight,
        passengerCount
    } = formData
    const previousLegs = usePrevious(legs)
    const {
        numberPassengersError,
    } = formErrorData
    const {resolvedLanguage} = i18n
    const [getAirlines, {
        data: airlinesResponse,
    }] = apiSlice.endpoints?.getAirlines.useLazyQuery();
    const [calculateEmissions, {
        data: resultCalculateEmissions,
        error: errorCalculateEmissions,
        isLoading: isCalculatingEmissions,
    }] = useCalculateEmissionsMutation()

    const _getCalculatedActivity = (hideResults: boolean, flight: ActivityFlightState | [ActivityFlightState, ActivityFlightState] | null) => {
        setHideCalculationResults(hideResults)
        getCalculatedActivity(flight)
    }

    useEffect(() => {
        if (disableCalculateButton) setDisableCalculateButton(false)
        if (resultCalculateEmissions) _getCalculatedActivity(true, null)
    }, [legs, returnFlight, aircrafts, passengerCount])

    useEffect(() => {
        if (airlinesResponse && airlineIataFromFlightNumber.current !== null) {
            const airlines = airlinesResponse.filter(airline => airline.iata === airlineIataFromFlightNumber.current)
            if (airlines.length === 1) {
                const airline = airlines[0]
                if (legIndexFromWhichFlightWithFlightNumberAndDateWasRequested.current >= 0) {
                    const upadtedLegs = _updateFlightDataOnCurrentLeg(
                        legIndexFromWhichFlightWithFlightNumberAndDateWasRequested.current,
                        legs,
                        "airline",
                        airline
                    )
                    _setFormData('legs', upadtedLegs)
                    legIndexFromWhichFlightWithFlightNumberAndDateWasRequested.current = -1
                }
            }
            airlineIataFromFlightNumber.current = null
        }
    }, [airlinesResponse])

    const _getAircraftTypes = useCallback(async () => {
        try {
            let headers = new Headers();
            const token = Cookies.get(COOKIE_NAME)
            headers.set('Authorization', `Bearer ${token}`);
            const response = await fetch(GET_AIRCRAFTS, {
                method: "GET",
                headers
            });
            if (response.ok) {
                const aircrafts: AircraftTypeResponse[] = await response.json();
                setAircrafts(aircrafts);
                setAircraftsNetworkError(false);
            } else {
                setAircraftsNetworkError(true);
                console.error(`Response not in range of 200 - 299. Response is ${response.status}`);
            }
        } catch (error) {
            setAircraftsNetworkError(true);
            console.error(`Something went wrong with getting airports: ${error}`);
        }
        return Promise.resolve()
    }, [])

    useEffect(() => {
        if (!isEdit || day !== null) _getAircraftTypes()
    }, [])

    useEffect(() => {
        // only set the formDate with flightToEdit on first render, otherwise, if user edits data and recalculates,
        // the newly entered data gets replaced with original flightToEdit
        if (isEdit && flightToEdit && isFirstRender) {
            const {legs, passengerCount, returnId} = flightToEdit
            _getAircraftTypes()
            _setFormData({
                legs: cloneDeep(legs).map(leg => _convertLegToLegForUi(leg)),
                passengerCount,
                returnFlight: Boolean(returnId),
            })
        }
    }, [flightToEdit, isEdit, day])

    useEffect(() => {
        if (isGeneralCalculatedFlightEmissionsResponse(errorCalculateEmissions)) {
            const {data: {status, flightActivities}} = errorCalculateEmissions
            let informativeToastShowed = false;
            const errorStrings: string[] = []
            flightActivities?.forEach(flight => {
                if (flight.errorCodes) flight.errorCodes.forEach(error => {
                    const {code} = error
                    const failedLeg = flightsForRequestFormat.current[flightsForRequestFormat.current.findIndex(leg => leg.index === flight.index)]
                    const {arrival, departure, flightDate, airline, aircraftType, flightNumber} = failedLeg
                    const airports = [departure, arrival].join(` ${ROUTE_SEPARATOR} `)
                    let formattedFlightDate = ''
                    if (flightDate) formattedFlightDate = formatDateString(DateTime.fromISO(flightDate), resolvedLanguage, false)
                    switch (code) {
                        case 601:
                            errorStrings.push(t(createErrorCodeString(code), {airport: departure}));
                            break;
                        case 602:
                            errorStrings.push(t(createErrorCodeString(code), {airline}));
                            break;
                        case 603:
                            errorStrings.push(t(createErrorCodeString(code), {aircraftType}));
                            break;
                        case 604:
                            errorStrings.push(t(createErrorCodeString(code), {airports}));
                            break;
                        case 605:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 606:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 607:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 608:
                            errorStrings.push(t(createErrorCodeString(code), {flightDate: formattedFlightDate}))
                            break;
                        case 609:
                            errorStrings.push(t(createErrorCodeString(code), {flightNumber}))
                            break
                        case 610:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break
                        case 611:
                            errorStrings.push(t(createErrorCodeString(code), {
                                flightNumber,
                                flightDate: formattedFlightDate
                            }))
                            break;
                        case 612:
                            errorStrings.push(t(createErrorCodeString(code), {flightNumber}))
                            break;
                        case 613:
                            errorStrings.push(t(createErrorCodeString(code), {
                                flightDate: formattedFlightDate,
                                flightNumber
                            }))
                            break;
                        case 614:
                            errorStrings.push(t(createErrorCodeString(code), {airline, aircraftType, airports}))
                            break;
                        case 615:
                            errorStrings.push(t(createErrorCodeString(code), {airline, airports}))
                            break;
                        case 616:
                            errorStrings.push(t(createErrorCodeString(code), {aircraftType, airports}))
                            break;
                        case 617:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 618:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 619:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 620:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 621:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 622:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 623:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break;
                        case 624:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break
                        case 625:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break
                        case 817:
                            errorStrings.push(t(createErrorCodeString(code), {aircraftType, airports}))
                            break;
                        case 830:
                            errorStrings.push(t(createErrorCodeString(code)))
                            break
                        default:
                            errorStrings.push(error.message)
                    }
                })
            })
            switch (status) {
                case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors:
                    errorStrings.push([
                        t("ERRORS.CALCULATION_PARTIAL_FAILURE"),
                        t("TOASTS.ERROR.PARTIAL_OR_COMPLETE_CALCULATION")
                    ].join("\n"))
                    break;
                case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed:
                    errorStrings.push([
                        t("ERRORS.CALCULATION_COMPLETE_FAILURE"),
                        t("TOASTS.ERROR.PARTIAL_OR_COMPLETE_CALCULATION")
                    ].join("\n"))
                    break;
                case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success:
                    break;
                default:
                    status satisfies never;
            }

            if (errorStrings.length > 0) {
                _renderToast(errorStrings.join("\n \n"), "warning")
                informativeToastShowed = true
            }
            if (!informativeToastShowed) toast(<ToastWithTextOnly
                    message={"TOASTS.ERRORS.GENERAL"}/>,
                {progress: undefined, type: "error"}
            )
            _setFlightsForStore(flightActivities)
        }
    }, [errorCalculateEmissions])

    useEffect(() => {
        if (resultCalculateEmissions) {
            setDisableCalculateButton(true)
            const {status, flightActivities, emissionKgCO2eTotal} = resultCalculateEmissions
            _getOnlyUsedAirportsForEmissionCalculation()
            if (emissionKgCO2eTotal !== null) {
                switch (status) {
                    case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors:
                        _renderToast([
                            t("ERRORS.CALCULATION_PARTIAL_FAILURE"),
                            t("TOASTS.ERROR.PARTIAL_OR_COMPLETE_CALCULATION")
                        ].join("\n"), 'warning')
                        break;
                    case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed:
                        _renderToast([
                            t("ERRORS.CALCULATION_COMPLETE_FAILURE"),
                            t("TOASTS.ERROR.PARTIAL_OR_COMPLETE_CALCULATION")
                        ].join("\n"), 'error')
                        break;
                    case CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success:
                        _renderToast("GENERAL.EMISSIONS_CALCULATED", "success")
                        break;
                    default:
                        status satisfies never;
                }
            }

            _setFlightsForStore(flightActivities)
        }

    }, [resultCalculateEmissions])

    const _setFlightsForStore = (flightActivities: FlightCalculatedEmissionsResponse[] | undefined) => {
        if (returnFlight) {
            const oneWayRoute = flightsForRequestFormat.current.slice(0, legs.length - 1)
            const returnRoute = cloneDeep(flightsForRequestFormat.current.slice(-(legs.length - 1))).map((flightRequest, index) => {
                flightRequest.index = index
                return flightRequest
            })
            const emissionsOneWayRoute = flightActivities?.slice(0, legs.length - 1) ?? null
            const emissionsReturnRoute = cloneDeep(flightActivities?.slice(-(legs.length - 1)))?.map((emissions, index) => {
                emissions.index = index
                return emissions
            }) ?? null
            let originalReturnId: string | null = null
            if (flightToEdit && activityHasReturnOption(flightToEdit)) originalReturnId = flightToEdit.returnId
            const commonActivityReturnFlightProps = _setCommonActivityProps(returnRoute, originalReturnId)
            const commonActivityOneWayFlightProps = _setCommonActivityProps(oneWayRoute, flightToEdit?.common.id)

            const returnFlight: ActivityFlightState = {
                common: {
                    ...commonActivityReturnFlightProps,
                    totalEmissions: emissionsReturnRoute,
                    originalTotalEmissions: cloneDeep(emissionsReturnRoute)
                },
                ..._setFlightProps(true, flightToEdit && activityHasReturnOption(flightToEdit) ? null : originalReturnId)
            }
            const oneWayFlight: ActivityFlightState = {
                common: {
                    ...commonActivityOneWayFlightProps,
                    totalEmissions: emissionsOneWayRoute,
                    originalTotalEmissions: cloneDeep(emissionsOneWayRoute)
                },
                ..._setFlightProps(false, returnFlight.common.id)
            }
            _getCalculatedActivity(false, [oneWayFlight, returnFlight])
        } else {
            const commonActivityProps = _setCommonActivityProps(flightsForRequestFormat.current, flightToEdit?.common.id)
            const emissions = flightActivities ?? null
            const flight: ActivityFlightState = {
                common: {
                    ...commonActivityProps,
                    totalEmissions: emissions,
                    originalTotalEmissions: cloneDeep(emissions)
                },
                ..._setFlightProps(false, null)
            }

            _getCalculatedActivity(false, flight)
        }
    }

    const _setFlightProps = (reverseLegs: boolean, returnId: string | null): Omit<ActivityFlightState, "common"> => {
        const reversedLegs = cloneDeep(legs)
        if (reverseLegs) {
            reversedLegs.forEach(leg => leg.id = uuidv4())
            reversedLegs.reverse()
        }
        return {
            legs: reverseLegs ? reversedLegs : legs,
            passengerCount,
            returnId,
        }
    }

    const _setFormData = <K extends keyof FormProps>(
        keyOrUpdates: K | Partial<FormProps>,
        payload?: FormProps[K]
    ) => setFormData({
        ...formData,
        ...(typeof keyOrUpdates === "string" ? {[keyOrUpdates]: payload} : keyOrUpdates)
    })

    const _setFormErrorData = <K extends keyof FormErrorProps>(key: K, payload: FormErrorProps[K]) => setFormErrorData({
        ...formErrorData,
        [key]: payload
    })

    const _getOnlyUsedAirportsForEmissionCalculation = () => {
        let onlyUsedAirports = true
        const firstLeg = getFirstUiLeg(legs)
        if (firstLeg) {
            const {airline, aircraftType, travelClass} = firstLeg
            if (airline || travelClass || aircraftType) onlyUsedAirports = false
            getUiStopover(legs).forEach(stop => {
                const {airline, aircraftType, travelClass} = stop
                if (airline || travelClass || aircraftType) onlyUsedAirports = false
            })
            setOnlyUsedAirportsForEmissionCalculation(onlyUsedAirports)
        }
    }

    const _setCommonActivityProps = (
        request: FlightCalculationRequestLeg[],
        activityId: string | null | undefined
    ): ActivityCommonProperties["common"] => {
        const id = activityId ?? uuidv4()
        return {
            ...setCommonFlightActivityProps(),
            day,
            editId: null,
            hideLoadBundleInformation: flightToEdit ? true : false,
            groupId: flightToEdit?.common.groupId ?? null,
            id: id ?? uuidv4(),
            semanticId: id,
            originalRequest: cloneDeep(request),
            request,
        }
    }

    const _convertLegToLegForUi = (leg: Leg): LegForUi => {
        return {
            ...leg,
            errors: {airport: false, flightNumber: false},
        }
    }

    const _showToastOnFlightViaFlightNumberAndDate = (countAirportsReplaced: number, flightNumberFromPreviousLegRemoved: boolean) => {
        let toastMessage = [t("TOASTS.SUCCESS.GETTING_FLIGHTS_VIA_NUMBER_AND_DATE")]
        if (flightNumberFromPreviousLegRemoved) toastMessage.push(t('TOASTS.SUCCESS.FLIGHT_NUMBER_REMOVED_DIFFERENT_AIRPORT'))
        if (countAirportsReplaced > 0) toastMessage.push(countAirportsReplaced === 1 ? t("TOASTS.SUCCESS.REPLACED_AIRPORT_SINGULAR") : t("TOASTS.SUCCESS.REPLACED_AIRPORT_PLURAL"))
        _renderToast(toastMessage.join("\n \n"), "success")
    }

    const _renderToast = (message: string, type: TypeOptions) => toast(<ToastWithTextOnly
            message={t(message)}/>,
        {progress: undefined, type}
    )

    const _isValidFlightNumber = (flightNumber: string | null | undefined) => {
        if (flightNumber) {
            const pattern = /^([A-Z]{2,3}|[A-Z]\d|\d[A-Z])(\d{1,4})$/;
            return flightNumber.length === 0 ? false : pattern.test(flightNumber)
        }
        return false
    }

    const _hasNoAirport = (leg: Leg | null) => {
        if (leg) return !Boolean(leg.airport)
        return true
    }

    /**
     * Fills the UI form with a new connection returned from the VER with a given flight number and date.
     * It also updates existing flights in the UI form. It removes (if there is one) flight number from
     * the previous leg as well as the following leg. The flight number of these legs must be removed,
     * because they are not part of the connection that the VER returned.
     * This means if the UI has this filled in
     *
     * IST 29.11.2024 TK38
     * JNB 29.11.2024 TK38
     * MPT 29.11.2024 TK38
     *
     * and the user searches for another flight with flight number and date directly after MPT 29.11.2024 TK38, it must
     * become
     *
     * IST 29.11.2024 TK38
     * JNB 29.11.2024 TK38
     * MPT 29.11.2024
     * MUC 11.12.2024 LH101
     * FRA 11.12.2024 LH101
     *
     * Why? Because the flight from MPT to MUC is not a flight carried out by the previous flight number!
     * The same is true for this case
     *
     * IST 29.11.2024 TK38
     * JNB 29.11.2024 TK38
     * MPT 29.11.2024 TK38
     *
     * Now, if the user searches for a flight before IST 29.11.2024 TK38, it must become
     *
     * MUC 11.12.2024 LH101
     * FRA 11.12.2024
     * JNB 29.11.2024 TK38
     * MPT 29.11.2024 TK38
     */
    const _fillFormBasedOnFlightNumberAndDate = (index: number, flights: FlightsResponse[]) => {
        allFlightsFoundViaFlightNumberAndDate.current.push(flights)
        let updatedLegs = cloneDeep(legs)
        let countAirportsReplaced = 0
        let groupIdFromLoadedBundleActivity: string | null
        if (flightToEdit !== null) {
            const {legs} = flightToEdit
            groupIdFromLoadedBundleActivity = legs[0].groupId
        }
        flights.forEach((flight, flightNumberIndex) => {
            const backendId = legs[flightNumberIndex].backendId ?? null
            const id = legs[flightNumberIndex].id ?? uuidv4()
            const departureIndex = index + flightNumberIndex
            const arrivalIndex = index + flightNumberIndex + 1
            const {
                departureAirport,
                arrivalAirport,
                airline,
                aircraftType,
                flightNumber,
                flightDate,
            } = flight
            const date = DateTime.fromISO(flightDate)
            let arrivalLegViaFlightNumber: LegForUi

            // if it is last leg of flight that is found via flight number and - date
            //  all data that comes along with flight number and - date must be removed
            if (flightNumberIndex !== flights.length - 1) {
                arrivalLegViaFlightNumber = _convertLegToLegForUi({
                    airport: arrivalAirport,
                    travelClass: null,
                    aircraftType,
                    airline,
                    date,
                    flightNumber,
                    ownAirportTransfer: false,
                    informationFromLoadedBundle: null,
                    groupId: groupIdFromLoadedBundleActivity,
                    backendId,
                    id
                })
            } else {
                arrivalLegViaFlightNumber = _convertLegToLegForUi({
                    airport: arrivalAirport,
                    travelClass: null,
                    aircraftType: null,
                    airline: null,
                    date,
                    flightNumber: null,
                    ownAirportTransfer: false,
                    informationFromLoadedBundle: null,
                    groupId: groupIdFromLoadedBundleActivity,
                    backendId,
                    id
                })
            }
            // The format of the returned flights is different then the one used in the UI
            // [{departure: MUC, arrival: IST}, {departure: IST, arrival: JFK}, {departure: JFK, arrival: BER}, {departure: BER, arrival: DOH}]
            // because of that, only on the very first leg (index === 0) the departure is used
            // on all the other legs (index > 0), arrival is used
            if (flightNumberIndex === 0) {
                const departureLegViaFlightNumber = _convertLegToLegForUi({
                    airport: departureAirport,
                    travelClass: null,
                    aircraftType,
                    airline,
                    date,
                    flightNumber,
                    ownAirportTransfer: false,
                    informationFromLoadedBundle: null,
                    groupId: groupIdFromLoadedBundleActivity,
                    backendId,
                    id
                })

                departureLegViaFlightNumber.travelClass = updatedLegs[departureIndex].travelClass
                updatedLegs[departureIndex] = departureLegViaFlightNumber
                if (
                    typeof updatedLegs[departureIndex] !== 'undefined' &&
                    legs[departureIndex].airport !== null &&
                    departureLegViaFlightNumber.airport !== null &&
                    (!isEqual(legs[departureIndex].airport, departureLegViaFlightNumber.airport))
                ) countAirportsReplaced += 1
            }

            if (typeof updatedLegs[arrivalIndex] === 'undefined') {
                updatedLegs.splice(arrivalIndex, 0, arrivalLegViaFlightNumber)
            } else {
                arrivalLegViaFlightNumber.travelClass = updatedLegs[arrivalIndex].travelClass
                updatedLegs[arrivalIndex] = arrivalLegViaFlightNumber
                if (
                    legs[arrivalIndex].airport !== null &&
                    arrivalLegViaFlightNumber.airport !== null &&
                    (!isEqual(legs[arrivalIndex].airport, arrivalLegViaFlightNumber.airport))
                ) countAirportsReplaced += 1
            }

            // If the leg after (arrivalIndex + 1) the last leg (arrivalIndex) has a flight number
            // this flight number must be removed, because it the flight is not part of the flight number
            if (flightNumberIndex === flights.length - 1) {
                const afterLegIndex = arrivalIndex + 1
                if (
                    updatedLegs[afterLegIndex] !== undefined &&
                    (updatedLegs[afterLegIndex].airport || updatedLegs[afterLegIndex].flightNumber)
                ) updatedLegs = _updateFlightDataOnCurrentLeg(arrivalIndex, updatedLegs, "flightNumber", null)
            }
        })
        // if the previous flight (index - 1) has a flight number
        // this flight number must be removed, if departure airport from new flight is different then arrival airport of previous flight
        // because it the flight is not part of the flight number
        const previousLegIndex = index - 1
        let flightNumberFromPreviousLegRemoved = false
        if (
            legs[index].airport?.name !== flights[0].departureAirport.name &&
            legs[previousLegIndex] &&
            legs[previousLegIndex].flightNumber !== null
        ) {
            updatedLegs = _updateFlightDataOnCurrentLeg(previousLegIndex, updatedLegs, "flightNumber", null)
            flightNumberFromPreviousLegRemoved = true
        }
        _setFormData('legs', updatedLegs)
        _showToastOnFlightViaFlightNumberAndDate(countAirportsReplaced, flightNumberFromPreviousLegRemoved)
        _resetModalRefs()
    }

    /**
     * Simple API request to get flights with a flight number and flight date from the VER.
     * Handles three cases:
     * 1. If a connection with one leg is found
     * 2. If there is a connection with more than one leg available for a given flight number and flight date. If that
     * is the case, a modal is shown and the user is forced to pick the legs that should be inserted into the UI.
     * 3. If the flight is a non-operational-code-sharing flight
     */
    const _requestFlightsByFlightNumberAndDate = async (index: number, leg: Leg) => {
        const {date, flightNumber} = leg
        if (date !== null) {
            const query = constructGetFlightByFlightNumberQuery(flightNumber, date)
            let headers = new Headers();
            const token = Cookies.get(COOKIE_NAME)
            headers.set('Authorization', `Bearer ${token}`);
            try {
                const response = await fetch(`${GET_FLIGHTS_BY_DATE_AND_AIRLINE}?${query}`,
                    {
                        method: "GET",
                        headers
                    });
                if (response.ok) {
                    const flights: FlightsResponse[] = await response.json();
                    const isCodeSharingFlight = flights.some(flight => flight.isNonOperationalCodeSharingFlight)
                    if (isCodeSharingFlight) {
                        _renderToast("TOASTS.ERROR.GETTING_FLIGHTS_CODE_SHARING_FLIGHT", "error")
                    } else {
                        if (flights.length > 1) {
                            setModalVisible(true)
                            legIndexFromWhichModalWasOpened.current = index
                            setModalContent(flights)
                        } else if (flights.length === 1) {
                            _fillFormBasedOnFlightNumberAndDate(index, flights)
                        } else if (flights.length === 0) {
                            if (leg.airport) {
                                _renderToast("TOASTS.WARNING.GETTING_FLIGHTS_VIA_NUMBER_AND_DATE_NO_FLIGHTS_FOUND_AIRPORTS_IN_UI", "warning")
                            } else {
                                _renderToast("TOASTS.WARNING.GETTING_FLIGHTS_VIA_NUMBER_AND_DATE_NO_FLIGHTS_FOUND_NO_AIRPORTS_IN_UI", "warning")
                            }
                            const updatedLegs = cloneDeep(legs)
                            _setFormData('legs', _updateFlightDataOnCurrentLeg(index, updatedLegs, "errors", {
                                airport: updatedLegs[index].errors.airport,
                                flightNumber: true
                            }))
                        } else {
                        }
                    }
                } else {
                    _renderToast("TOASTS.ERROR.GETTING_FLIGHTS_VIA_NUMBER_AND_DATE", "error")
                    console.error(`Response not in range of 200 - 299. Response is ${response.status}`)
                }
            } catch (error) {
                _renderToast("TOASTS.ERROR.GETTING_FLIGHTS_VIA_NUMBER_AND_DATE", "error")
                console.error(`Something went wrong with getting flights via flight number and flight date: ${error}`)
            }
        }
    }

    /**
     * Extracts the flight number and flight date from the user input and then requests the airline based on
     * the flight number as well as requests possible flights that the VER can find with the given flight
     * number and flight date
     */
    const _setFlightsWithDateAndFlightNumber = async (index: number) => {
        if (previousLegs) {
            if (previousLegs[index].flightNumber !== legs[index].flightNumber || !isEqual(previousLegs[index].date, legs[index].date)) {
                gettingFlightsByFlightNumberAndDate.current = true
                const currentLeg = legs[index]
                const {flightNumber} = currentLeg
                if (_isValidFlightNumber(flightNumber) && flightNumber !== null) {
                    if (flightNumber.length <= 6) {
                        airlineIataFromFlightNumber.current = flightNumber.slice(0, 2)
                    } else if (flightNumber.length <= 7) {
                        airlineIataFromFlightNumber.current = flightNumber.slice(0, 3)
                    } else {
                        airlineIataFromFlightNumber.current = null
                    }
                    if (airlineIataFromFlightNumber.current !== null) {
                        legIndexFromWhichFlightWithFlightNumberAndDateWasRequested.current = index
                        getAirlines(airlineIataFromFlightNumber.current)
                    } else {
                        legIndexFromWhichFlightWithFlightNumberAndDateWasRequested.current = -1
                    }
                    await _requestFlightsByFlightNumberAndDate(index, currentLeg)
                }
                gettingFlightsByFlightNumberAndDate.current = false
            }
        }
    }

    const _legsMatchFlightFoundViaFlightNumberAndDate = (leg: LegForUi, isLastLeg: boolean) => {
        for (const element of allFlightsFoundViaFlightNumberAndDate.current) {
            const flightsViaFlightNumberAndDate = element
            for (let j = 0; j < flightsViaFlightNumberAndDate.length; j++) {
                const flightViaFlightNumberAndDate = flightsViaFlightNumberAndDate[j]
                if (isLastLeg) {
                    if (isEqual(leg.airport, flightViaFlightNumberAndDate.arrivalAirport)) return true
                } else {
                    if (
                        // The format of the returned flights is different from the one used in the UI
                        // [{departure: MUC, arrival: IST}, {departure: IST, arrival: JFK}, {departure: JFK, arrival: BER}, {departure: BER, arrival: DOH}]
                        // because of that, only on the very first leg (j === 0) the departure is used
                        // on all the other legs (j > 0), arrival is used
                        isEqual(leg.airport, j === 0 ? flightViaFlightNumberAndDate.departureAirport : flightsViaFlightNumberAndDate[j - 1].arrivalAirport) &&
                        isEqual(leg.aircraftType, flightViaFlightNumberAndDate.aircraftType) &&
                        isEqual(leg.flightNumber, flightViaFlightNumberAndDate.flightNumber)
                    ) return true
                }
            }
        }
        return false
    }

    const _matchPreviouslyFlightsViaFlightNumberAndFlightDate = (updatedLegs: LegForUi[]) => {
        const matchingLegsIndices: number[] = [];
        updatedLegs.forEach((leg, index) => {
            if (index === updatedLegs.length - 1) {
                _legsMatchFlightFoundViaFlightNumberAndDate(leg, true) && matchingLegsIndices.push(index)
            } else {
                _legsMatchFlightFoundViaFlightNumberAndDate(leg, false) && matchingLegsIndices.push(index)
            }
        })

        if (matchingLegsIndices.length > 0) {
            updatedLegs.forEach((_, currentLegIndex) => {
                const nextLegIndex = currentLegIndex + 1;
                if (
                    !matchingLegsIndices.includes(currentLegIndex) ||
                    !matchingLegsIndices.includes(nextLegIndex)
                ) updatedLegs = _updateFlightDataOnCurrentLeg(currentLegIndex, updatedLegs, 'flightNumber', null)
            });
        }
        return updatedLegs
    }

    const _updateFlightDataOnCurrentLeg = <K extends keyof LegForUi>(
        index: number,
        legs: Array<LegForUi>,
        key: K,
        payload: LegForUi[K],
    ): Array<LegForUi> => legs.map((leg, idx) => {
        if (idx === index) {
            const updatedLeg = {
                ...leg,
                [key]: payload,
            }
            if (key === "flightNumber") {
                if (
                    payload === null ||
                    (typeof payload === 'string' && payload.length === 0)
                ) {
                    updatedLeg.errors.flightNumber = false
                    return updatedLeg
                }
                return updatedLeg
            } else if (key === "airport") {
                if (isAirportResponse(payload)) {
                    updatedLeg.errors.airport = false
                    return updatedLeg
                }
                return updatedLeg
            } else {
                return updatedLeg
            }
        }
        return leg
    })

    const _setAirline = (index: number, airline: AirlineResponse | null, leg: LegForUi) => {
        const {flightNumber} = leg
        let updatedLegs = _updateFlightDataOnCurrentLeg(index, legs, "airline", airline)
        if (flightNumber && !isEqual(leg.airline, airline)) updatedLegs = _updateFlightDataOnCurrentLeg(index, legs, "flightNumber", null)
        updatedLegs = _matchPreviouslyFlightsViaFlightNumberAndFlightDate(updatedLegs)
        _setFormData('legs', updatedLegs)
    }

    const _setAirport = (index: number, airport: AirportResponse | null, leg: LegForUi) => {
        const {flightNumber} = leg
        let updatedLegs = _updateFlightDataOnCurrentLeg(index, legs, "airport", airport)
        if (flightNumber && !isEqual(leg.airport, airport)) {
            updatedLegs = _updateFlightDataOnCurrentLeg(index, updatedLegs, "flightNumber", null)
            updatedLegs = _updateFlightDataOnCurrentLeg(index, updatedLegs, "errors", {
                airport: updatedLegs[index].errors.airport,
                flightNumber: false
            })
        }
        const previousLegIndex = index - 1
        const previousLeg = legs[previousLegIndex]
        if (typeof previousLeg !== 'undefined') updatedLegs = _updateFlightDataOnCurrentLeg(previousLegIndex, updatedLegs, 'flightNumber', null)
        _setFormData('legs', updatedLegs)
    }

    const _setAircraftType = (index: number, aircraftType: AircraftTypeResponse | null, leg: LegForUi) => {
        const {flightNumber} = leg
        let updatedLegs = _updateFlightDataOnCurrentLeg(index, legs, "aircraftType", aircraftType)
        if (flightNumber && !isEqual(leg.aircraftType, aircraftType)) updatedLegs = _updateFlightDataOnCurrentLeg(index, updatedLegs, "flightNumber", null)
        updatedLegs = _matchPreviouslyFlightsViaFlightNumberAndFlightDate(updatedLegs)
        _setFormData('legs', updatedLegs)
    }

    const _removeStopover = (index: number) => {
        let updatedLegs = legs.filter((_, idx) => idx !== index)
        updatedLegs = _matchPreviouslyFlightsViaFlightNumberAndFlightDate(updatedLegs)
        _setFormData('legs', updatedLegs)
        const indexToUpdateOwnAirportTransfer = index - 1
        if (updatedLegs[indexToUpdateOwnAirportTransfer]?.ownAirportTransfer) _setFormData('legs',
            _updateFlightDataOnCurrentLeg(
                indexToUpdateOwnAirportTransfer,
                updatedLegs, "ownAirportTransfer",
                _showOwnAirportTransfer(
                    indexToUpdateOwnAirportTransfer,
                    updatedLegs
                )
            )
        )
    }

    const _renderArrivalAirport = (index: number, leg: LegForUi) => {
        const {airport, errors} = leg
        return <AutocompleteQuery
            placeholder={"ACTIVITIES.FLIGHT.PLACEHOLDER_AIRPORT"}
            aria-errormessage={"ERRORS.FLIGHT.SELECT_AIRPORT_FROM_LIST"}
            label={"GENERAL.AIRPORT"}
            required={true}
            containerStyle={{width: "100%"}}
            containerClasses={'choose-compensateFlight-connection-last-leg-width'}
            defaultValue={airport}
            error={errors.airport}
            queryType={AUTOCOMPLETE_QUERY_TYPE.airport}
            type={AUTOCOMPLETE_ACTIVITY_TYPE.flight}
            listBoxClasses={'choose-compensateFlight-connection-last-leg-listbox-width'}
            setSelection={airport => _setAirport(index, airport as AirportResponse, leg)}/>
    }

    const _renderFlightConnection = (index: number, leg: LegForUi) => <FlightSelector
        disableDateRangePicker={disableDate}
        aircrafts={aircrafts}
        aircraftsNetworkError={aircraftsNetworkError}
        travelClasses={TRAVEL_CLASSES}
        airportError={leg.errors.airport}
        flightNumberError={leg.errors.flightNumber}
        airportLabel={"GENERAL.AIRPORT"}
        required={index === 0 || index === legs.length - 1}
        leg={leg}
        minDate={minDate}
        maxDate={maxDate}
        openToDate={openToDate}
        onBlurFlightNumber={async () => !gettingFlightsByFlightNumberAndDate.current && _setFlightsWithDateAndFlightNumber(index)}
        setFlightNumber={flightNumber => _setFormData('legs', _updateFlightDataOnCurrentLeg(index, legs, "flightNumber", flightNumber))}
        onBlurFlightDate={async () => !gettingFlightsByFlightNumberAndDate.current && _setFlightsWithDateAndFlightNumber(index)}
        setDate={date => _setFormData('legs', _updateFlightDataOnCurrentLeg(index, legs, "date", date))}
        setAirline={airline => _setAirline(index, airline, leg)}
        setAirport={airport => _setAirport(index, airport, leg)}
        setAircraftType={aircraftType => _setAircraftType(index, aircraftType, leg)}
        setTravelClass={travelClass => _setFormData('legs', _updateFlightDataOnCurrentLeg(index, legs, "travelClass", travelClass))}/>

    const _showOwnAirportTransfer = (index: number, allLegs: Leg[]) => {
        if (
            (allLegs[index - 1]?.ownAirportTransfer) ||
            allLegs[index + 1].ownAirportTransfer ||
            index === allLegs.length - 2
        ) return false
        return true
    }

    const _renderStopover = (index: number, leg: LegForUi) => {
        const showOwnAirportTransfer = _showOwnAirportTransfer(index, legs)
        return <div
            style={{width: "100%"}} key={index}>
            <Text
                style={{marginBottom: theme.spacer.small}}
                weight={'bold'}
                color={"primary"}
                variant={'subtitleLarge'}>
                {`${index}. ${t("GENERAL.STOPOVER")}`}
            </Text>
            <div
                className={'choose-flight-stopover-wrapper'}>
                {_renderFlightConnection(index, leg)}
                <div
                    className={'choose-flight-airport-transfer-checkbox'}>
                    <CheckboxInput
                        containerStyle={{visibility: showOwnAirportTransfer ? "visible" : "hidden"}}
                        checked={Boolean(legs[index].ownAirportTransfer)}
                        onChange={() => _setFormData('legs', _updateFlightDataOnCurrentLeg(index, legs, "ownAirportTransfer", !leg.ownAirportTransfer))}
                        label={"ACTIVITIES.FLIGHT.AIRPORT_TRANSFER"}
                        hideLabel={true}/>
                    <Text
                        style={{visibility: showOwnAirportTransfer ? "visible" : "hidden"}}>ACTIVITIES.FLIGHT.AIRPORT_TRANSFER</Text>
                    <IconButton
                        tooltip={"TOOLTIP.FLIGHT.REMOVE_STOPOVER"}
                        icon={'minus'}
                        onClick={() => _removeStopover(index)}
                        variant={'warning'}
                        size={'medium'}
                    />
                </div>
            </div>
        </div>
    }

    const _renderNumberOfPassengerFlightsReturnFlight = () => {
        let {minNumberFlights, maxNumberPassenger, minNumberPassenger} = FLIGHT_PROPERTIES
        if (maxPersons !== null) maxNumberPassenger = maxPersons
        const WIDTH_INPUT = 30;
        return <Fieldset
            legend={"LABELS.FLIGHT.NUMBER_FLIGHT_PERSONS_RETURN"}
            showLegend={false}
            fieldsetClasses={clsx("form-row", 'choose-flight-additional-options-wrapper')}>
            <NumberInput
                error={numberPassengersError}
                aria-errormessage={t("ERRORS.COMMON.BASE_MIN_MAX_VALUE_EXCEEDED_MESSAGE", {
                    min: minNumberFlights,
                    max: maxNumberPassenger,
                    words: t("ERRORS.FLIGHT.MIN_MAX_PASSENGERS_EXCEEDED")
                })}
                min={minNumberPassenger}
                max={maxNumberPassenger}
                injectMinimum={true}
                injectMaximum={true}
                required={true}
                label={"GENERAL.PASSENGER"}
                onBlur={event => {
                    const {target: {valueAsNumber}} = event
                    _setFormData("passengerCount", valueAsNumber)
                }}
                onChange={event => {
                    const {target: {valueAsNumber}} = event
                    _setFormData("passengerCount", valueAsNumber)
                }}
                containerStyle={{width: `${WIDTH_INPUT}px`}}
                value={passengerCount}/>
            <CheckboxInput
                inputStyle={{marginTop: '13px'}}
                checked={returnFlight}
                label={"ACTIVITIES.FLIGHT.RETURN_FLIGHT_SINGULAR"}
                onChange={() => _setFormData('returnFlight', !returnFlight)}/>
            <Text
                style={{color: theme.colors.primary}}
                variant={'subtitleLarge'}
                classes={'choose-flight-additional-options-information-text'}>
                {createFlightTripPassengersCountString(
                    returnFlight,
                    0,
                    passengerCount,
                    false,
                    i18n,
                )}
            </Text>
            {
                returnFlight && legs.some(leg => leg.flightNumber) && <Text
                    style={{width: '100%'}}
                    color={"warningLight"}
                    variant={"subtitleLarge"}>
                    HINTS.FLIGHT.RETURN_FLIGHT_FLIGHT_NUMBER
                </Text>
            }
            {returnFlight && fromWhichScreen === PATH_SCREEN_STRING.journey && <Text
                style={{width: '100%', marginTop: theme.spacer.large}}
                color={"warningLight"}
                variant={"subtitleLarge"}>
                {t("HINTS.FLIGHT.RETURN_FLIGHT_SPLIT", {day})}
            </Text>}
        </Fieldset>
    }

    const _renderResultString = (
        status: CalculatedEmissionsBackendResponse["status"],
        emissionKgCO2eTotal: CalculatedEmissionsBackendResponse["emissionKgCO2eTotal"]
    ) => <>
        <Text variant={"headlineMedium"}>
            {
                status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed ?
                    generateTotalCo2EAndCo2EPerPersonString(emissionKgCO2eTotal!, flightsForRequestFormat.current[0].passengerCount, resolvedLanguage, i18n) :
                    `${NO_DATA_STRING} ${KG_CO2}`
            }
        </Text>
        {onlyUsedAirportsForEmissionCalculation &&
            <Text
                color={'warningLight'}>HINTS.EMISSION_CALCULATION.FLIGHT.BETTER_CALCULATION_POSSIBILITY_GENERAL</Text>}
    </>

    const _renderResult = () => {
        if (resultCalculateEmissions && !hideCalculationResults) {
            const {status, emissionKgCO2eTotal} = resultCalculateEmissions
            return _renderResultString(status, emissionKgCO2eTotal)
        }

        if (isGeneralCalculatedFlightEmissionsResponse(errorCalculateEmissions)) {
            const {data: {status, emissionKgCO2eTotal}} = errorCalculateEmissions
            return _renderResultString(status, emissionKgCO2eTotal)
        }

        return null
    }

    const _setCommonFlightRequestProps = (leg: Leg, index: number, departureAirportIata: string, arrivalAirportIata: string): FlightCalculationRequestLeg => {
        const {flightNumber, date} = leg
        let flightDate: string | undefined = ''
        if (flightNumber && date) {
            flightDate = convertDateTimeToFlightDate(date)
        } else {
            flightDate = undefined
        }
        return {
            index,
            type: CALCULATION_TYPE.flight,
            passengerCount,
            airline: leg.airline ? leg.airline?.iata : null,
            travelClass: leg.travelClass,
            aircraftType: convertUiAircraftTypeToApiAircraftType(leg.aircraftType),
            flightCount: 1,
            departure: departureAirportIata,
            arrival: arrivalAirportIata,
            flightDate: flightDate ?? null,
            flightNumber: flightNumber ?? null,
        }
    };

    const _getIndicesWithOwnAirportTransfer = (legs: LegForUi[]) => legs
        .map((leg, index) => leg.ownAirportTransfer ? index : -1)
        .filter(index => index !== -1);

    const _convertLegsIntoRequestFormat = () => {
        const allLegs: FlightCalculationRequestLeg[] = []
        let flightIndex = 0
        const legIndexWithOwnAirportTransfer = _getIndicesWithOwnAirportTransfer(legs)

        for (let i = 0; i < legs.length; i++) {
            if (i === legs.length - 1) break
            if (legIndexWithOwnAirportTransfer.includes(i)) i += 1
            let departureLeg = {} as LegForUi
            let arrivalLeg = {} as LegForUi
            departureLeg = legs[i];
            arrivalLeg = legs[i + 1];

            allLegs.push(
                _setCommonFlightRequestProps(
                    departureLeg,
                    flightIndex,
                    departureLeg.airport?.iata!!,
                    arrivalLeg.airport?.iata!!
                )
            )
            flightIndex += 1
        }
        if (returnFlight) {
            const reversedLegs = cloneDeep(allLegs).reverse()
            reversedLegs.forEach((leg, index) => {
                const {arrival, departure} = reversedLegs[index]
                leg.arrival = departure
                leg.departure = arrival
                leg.flightDate = null
                leg.flightNumber = null
                leg.index = allLegs.length - 1 + 1
                allLegs.push(leg)
            })
        }
        return allLegs
    }

    const _calculateEmissions = async () => {
        flightsForRequestFormat.current = []
        flightsForRequestFormat.current = _convertLegsIntoRequestFormat()
        // flightsForRequestFormat.current = _triggerErrors(11)
        try {
            calculateEmissions({flightActivities: flightsForRequestFormat.current})
        } catch (error) {
            console.error(`Something went wrong when calculating emissions for flights: ${error}`);
        }
    }

    const _evaluateForm = () => {
        // general form errors
        let duplicatedAirportErrorIndex: number | null = null
        const {minNumberPassenger, maxNumberPassenger} = FLIGHT_PROPERTIES
        const updatedLegs = cloneDeep(legs)
        legs.forEach((leg, index) => {
            const nextLegIndex = index + 1
            const {ownAirportTransfer, airport} = leg
            // duplicated airports
            if (!ownAirportTransfer && legs[nextLegIndex]) {
                if (isEqual(airport, legs[nextLegIndex].airport)) {
                    updatedLegs[nextLegIndex].errors.airport = true
                    duplicatedAirportErrorIndex = nextLegIndex
                }
            }
            if (
                duplicatedAirportErrorIndex === null ||
                index !== duplicatedAirportErrorIndex
            ) updatedLegs[index].errors.airport = _hasNoAirport(leg)
        })
        const numberOfPassengersError = !(passengerCount >= minNumberPassenger && passengerCount <= maxNumberPassenger)
        const anyErrors = updatedLegs.some(leg => leg.errors.airport || leg.errors.flightNumber)
        _setFormData('legs', updatedLegs)
        _setFormErrorData("numberPassengersError", numberOfPassengersError);
        if (!(
            anyErrors ||
            numberOfPassengersError
        )) _calculateEmissions();
    }

    const _renderLegs = () => {
        const allLegs: ReactNode[] = legs.map((leg, index) => {
            if (index === 0) {
                return <Fieldset
                    key={index}
                    role={"group"}
                    fieldsetClasses={'calculate-activity-stops-wrapper'}
                    showLegend={true}
                    required={true}
                    legend={"ACTIVITIES.FLIGHT.DEPARTURE_AIRPORT"}>
                    {_renderFlightConnection(index, leg)}
                </Fieldset>
            } else if (index === legs.length - 1) {
                return <Fieldset
                    key={index}
                    fieldsetClasses={'calculate-activity-stops-wrapper'}
                    fieldsetStyles={{justifyContent: 'flex-start',}}
                    showLegend={true}
                    required={true}
                    legend={"ACTIVITIES.FLIGHT.ARRIVAL_AIRPORT"}>
                    {_renderArrivalAirport(index, leg)}
                </Fieldset>
            } else {
                return <Fieldset
                    key={index}
                    role={"group"}
                    fieldsetClasses={'calculate-activity-stops-wrapper'}
                    showLegend={index === 1}
                    required={false}
                    legend={getUiStopover(legs).length > 0 ? "GENERAL.STOPOVERS" : "GENERAL.STOPOVER"}>
                    {_renderStopover(index, leg)}
                </Fieldset>
            }
        })

        allLegs.splice(
            allLegs.length - 1,
            0,
            <IconAndText
                disabled={allLegs.length >= MAX_STOPOVERS}
                tooltip={allLegs.length >= MAX_STOPOVERS ? t("TOOLTIP.FLIGHT.MAX_STOPOVERS", {maxStopovers: MAX_STOPOVERS}) : undefined}
                key={allLegs.length}
                text={"ACTIVITIES.FLIGHT.ADD_VIA_AIRPORT"}
                onClick={() => _setFormData('legs', [...legs.slice(0, -1), setEmptyLeg(uuidv4()), ...legs.slice(-1)])}
                iconColor={theme.colors.primary}
                icon={'plus'}/>
        )
        return allLegs
    }

    const _resetModalRefs = () => {
        selectedFlightsViaFlightNumberAndDateFromModal.current = []
        legIndexFromWhichModalWasOpened.current = 1
    }

    const _onCloseModal = () => {
        _resetModalRefs()
        setModalVisible(false)
    }

    const _onConfirmSelectAmbiguousFlights = () => {
        setModalVisible(false)
        if (selectedFlightsViaFlightNumberAndDateFromModal.current.length > 0) _fillFormBasedOnFlightNumberAndDate(legIndexFromWhichModalWasOpened.current, selectedFlightsViaFlightNumberAndDateFromModal.current)
    }

    return <>
        <Text style={{marginBottom: theme.spacer.large}}>
            ACTIVITIES.FLIGHT.DESCRIPTION
        </Text>
        <div
            onKeyDown={event => {
                if (event.key === "Enter") {
                    if (!event.defaultPrevented) {
                        event.preventDefault()
                        _evaluateForm()
                    }
                }
            }}
            className={'choose-flight-route-selection-container'}>
            {_renderLegs()}
            {_renderNumberOfPassengerFlightsReturnFlight()}
            <div className={'calculate-activity-bottom-buttons'}>
                {_renderResult()}
                <div className={'choose-flight-buttons-wrapper'}>
                    {!isEdit && <TextButton onClick={resetForm}>GENERAL.RESET</TextButton>}
                    <TextButton
                        disabled={disableCalculateButton}
                        onClick={_evaluateForm}>
                        {isEdit ? "GENERAL.CALCULATE_AGAIN" : "GENERAL.CALCULATE"}
                    </TextButton>
                </div>
            </div>
            {isCalculatingEmissions && <DelayedSpinner fullscreen={true} delayMs={500}/>}
        </div>
        <Modal
            headline={"ACTIVITIES.FLIGHT.CHOOSE_FLIGHTS"}
            show={modalVisible}
            closeButtonText={"GENERAL.ABORT"}
            confirmButtonText={"GENERAL.OK"}
            onEnterKeyPressed={_onConfirmSelectAmbiguousFlights}
            onConfirm={() => _onConfirmSelectAmbiguousFlights()}
            onBackdropClick={_onCloseModal}
            onClose={_onCloseModal}
            showPaper={true}>
            <AmbiguousFlightList
                setFlight={selectedFlight => {
                    const selectedFlights = selectedFlightsViaFlightNumberAndDateFromModal.current
                    const indexOfSelectedFlight = findIndex(selectedFlights, (flight: FlightsResponse) => isEqual(flight, selectedFlight))
                    if (indexOfSelectedFlight !== -1) {
                        selectedFlightsViaFlightNumberAndDateFromModal.current = (selectedFlights.slice(0, indexOfSelectedFlight).concat(selectedFlights.slice(indexOfSelectedFlight + 1)))
                    } else {
                        selectedFlightsViaFlightNumberAndDateFromModal.current = ([...selectedFlights, selectedFlight])
                    }
                }}
                flights={modalContent}
            />
        </Modal>
    </>
})

export default ChooseFlight